import React, { useState } from "react";
import { Snowflake, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// Congélation d'un lot au plus tard le jour de sa DLC → DDM = date de congélation + 90 j.
export const FreezeLotDialog = ({ lot, productName, onDone, testid }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const dlc = (lot?.dlc || "").slice(0, 10);
  const expired = !dlc || date > dlc;

  const openDialog = () => { setQty(String(lot?.quantity ?? "")); setDate(today); setNotes(""); setOpen(true); };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lots/${lot.id}/freeze`, { quantity: Number(qty), frozen_at: date, notes });
      toast.success(`Lot ${data.lot_number} congelé — DDM ${fmtDate(data.ddm)}`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="ghost" size="icon" title="Congeler (avant DLC)" onClick={openDialog} data-testid={testid}>
        <Snowflake className="w-4 h-4 text-[#2E6A8E]" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Snowflake className="w-5 h-5 text-[#2E6A8E]" /> Congeler le lot {lot?.lot_number}</DialogTitle>
            <DialogDescription>{productName} · DLC {fmtDate(dlc)} · {lot?.quantity} disponible(s)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Quantité</Label>
                <Input type="number" min="1" max={lot?.quantity} value={qty} onChange={(e) => setQty(e.target.value)} data-testid={`${testid}-qty`} />
              </div>
              <div>
                <Label className="mb-1.5 block">Congelé le</Label>
                <Input type="date" value={date} max={dlc || undefined} onChange={(e) => setDate(e.target.value)} data-testid={`${testid}-date`} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Notes (optionnel)</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ex. usage interne cuisine" data-testid={`${testid}-notes`} />
            </div>
            {expired ? (
              <p className="text-xs p-2 rounded bg-[#FCEEEA] text-[#A04E2C]" data-testid={`${testid}-expired`}>DLC dépassée : congélation interdite, le lot doit être détruit.</p>
            ) : (
              <p className="text-xs p-2 rounded bg-[#EAF1F6] text-[#2E6A8E]" data-testid={`${testid}-ddm`}>
                La DLC sera remplacée par une DDM au <strong>{fmtDate(addDays(date, 90))}</strong> (congélation + 90 jours). Étiqueter avec la date de congélation. Inscrit au registre HACCP.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={busy || expired || !(Number(qty) > 0) || Number(qty) > (lot?.quantity || 0)} className="bg-[#2E6A8E] hover:bg-[#245570]" data-testid={`${testid}-submit`}>
              {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Snowflake className="w-4 h-4 mr-1.5" />} Congeler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FreezeLotDialog;
